import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import HeroSection from "./components/HeroSection";
import CategoryCards from "./components/CategoryCards";
import PortfolioPreview from "./components/PortfolioPreview";
import ClientLogos from "./components/ClientLogos";
import HubSpotChat from "./components/HubSpotChat";
import Footer from "./components/Footer";

const AdaptiveHomepageHub = () => {
  const [currentTheme, setCurrentTheme] = useState("realestate");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading and theme detection
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Apply theme to document root for global theme switching
    const root = document.documentElement;
    root.setAttribute("data-theme", currentTheme);

    // Update CSS custom properties based on theme
    const themeColors = {
      events: {
        "--color-accent": "#A08D7C",
        "--color-accent-foreground": "#F5F5F2",
      },
      realestate: {
        "--color-accent": "#B08D57",
        "--color-accent-foreground": "#F5F5F2",
      },
      marketing: {
        "--color-accent": "#A08D7C",
        "--color-accent-foreground": "#F5F5F2",
      },
      design: {
        "--color-accent": "#B08D57",
        "--color-accent-foreground": "#F5F5F2",
      },
      default: {
        "--color-accent": "#B08D57",
        "--color-accent-foreground": "#F5F5F2",
      },
    };

    const colors = themeColors?.[currentTheme] || themeColors?.default;
    Object.entries(colors)?.forEach(([property, value]) => {
      root.style?.setProperty(property, value);
    });
  }, [currentTheme]);

  const handleThemeChange = () => {
    // Lock theme to architectural (real estate) to prevent slide-induced shifts
    if (currentTheme !== "realestate") setCurrentTheme("realestate");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-primary text-3xl font-brand tracking-wide">
            <span>Skycrest</span>
            <span className="text-accent">Films</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Skycrest Studios - Visual Storytelling That Transforms</title>
        <meta
          name="description"
          content="Dynamic video production portfolio platform with adaptive brand identity. Specializing in events, real estate, marketing, and design fiction across diverse industries."
        />
        <meta
          name="keywords"
          content="video production, visual storytelling, wedding videography, real estate marketing, brand campaigns, design fiction, Los Angeles"
        />
        <meta
          property="og:title"
          content="Skycrest Studios - Visual Storytelling That Transforms"
        />
        <meta
          property="og:description"
          content="Professional video production services across events, real estate, marketing, and design fiction. Creating visual experiences that elevate your brand."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://skycreststudios.com/adaptive-homepage-hub"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Skycrest Studios - Visual Storytelling That Transforms"
        />
        <meta
          name="twitter:description"
          content="Professional video production services that adapt to your industry needs. From romantic weddings to bold marketing campaigns."
        />
        <link
          rel="canonical"
          href="https://skycreststudios.com/adaptive-homepage-hub"
        />
      </Helmet>

      <div
        className={`min-h-screen bg-background transition-all duration-1000 theme-${currentTheme}`}
      >
        <Header />

        <main>
          <HeroSection
            currentTheme={currentTheme}
            onThemeChange={handleThemeChange}
          />

          <CategoryCards
            currentTheme={currentTheme}
            onThemeChange={handleThemeChange}
          />

          <PortfolioPreview currentTheme={currentTheme} />

          <ClientLogos currentTheme={currentTheme} />
        </main>

        <Footer currentTheme={currentTheme} />

        <HubSpotChat currentTheme={currentTheme} />
      </div>
    </>
  );
};

export default AdaptiveHomepageHub;
